import * as React from 'react'

import PerformanceBreakdownView from './performanceBreakdownView'
import SmartGroupsView from './smartGroupsView'
import {
  ADMIN_SKILLS_REPORT_MOMENTUM_SCORE_GOAL,
  AGGREGATION_SKILL,
  AGGREGATION_STUDENT,
  ALL_CATEGORIES_OPTION,
  ALL_STUDENTS_OPTION,
  ALL_SUB_CATEGORIES_OPTION,
  PERFORMANCE_BREAKDOWN_VIEW,
  ResultRow,
  SMART_GROUPS_VIEW,
  SkillConcept,
  SubConcept,
  VIEW_LABEL_BY_VALUE,
  getUnpracticedEntityLabel,
  groupByDropdownOptions,
  payloadMatchesHash,
  supplementMissingRows,
} from './helpers'

import {
  ReportHeaderButtons,
  ReportViewToggle,
  SelectTeacherPrompt,
  StudentFilterDropdown,
  useMomentumScoreGoalModal,
} from '../../adminMomentumReports/components'
import {
  useAppliedTeacher,
  usePusherMessage,
  useSnackbar,
  useTeacherStudents,
} from '../../adminMomentumReports/hooks'
import { FULL, forwardSlash, restrictedPage, mapItemsIfNotAll, } from '../../shared'
import {
  Checkbox,
  DropdownInput,
  Snackbar,
  Spinner,
  filterIcon,
  getPageTitle,
} from '../../../Shared/index'
import { DropdownObjectInterface } from '../../../Staff/interfaces/evidenceInterfaces'
import { requestGet, requestPost } from '../../../../modules/request'

const QUERY_ENDPOINT = '/admin_skills_reports/report'
const CONCEPTS_ENDPOINT = '/admin_skills_reports/concepts'
const STUDENTS_ENDPOINT = '/admin_skills_reports/students'
const PUSHER_EVENT_KEY = 'admin-skills-report-cached'

const AdminSkillsReportsContainer = ({
  accessType,
  loadingFilters,
  pusherChannel,
  searchCount,
  selectedClassrooms,
  availableClassrooms,
  selectedTimeframe,
  selectedGrades,
  availableGrades,
  selectedSchools,
  selectedTeachers,
  availableTeachers,
  applyTeacherSelection,
  openMobileFilterMenu,
  horizontalFilters,
}) => {
  document.title = getPageTitle('Premium Hub', 'Premium Reports | Skills Report')

  const [groupByValue, setGroupByValue] = React.useState<DropdownObjectInterface>(groupByDropdownOptions[0])
  const [activeView, setActiveView] = React.useState<string>(SMART_GROUPS_VIEW)
  const [conceptId, setConceptId] = React.useState<number | null>(null)
  const [rows, setRows] = React.useState<ResultRow[] | null>(null)
  const [loading, setLoading] = React.useState<boolean>(true)
  const [ell, setEll] = React.useState<boolean>(false)
  const [allConcepts, setAllConcepts] = React.useState<SkillConcept[]>([])
  const [allSubConcepts, setAllSubConcepts] = React.useState<SubConcept[]>([])
  const [skillCategoryValue, setSkillCategoryValue] = React.useState<DropdownObjectInterface>(ALL_CATEGORIES_OPTION)
  const [subCategoryValue, setSubCategoryValue] = React.useState<DropdownObjectInterface>(ALL_SUB_CATEGORIES_OPTION)
  const [subConceptId, setSubConceptId] = React.useState<number | null>(null)
  const [hasExplicitlySelectedCategory, setHasExplicitlySelectedCategory] = React.useState<boolean>(false)

  const pusherMessage = usePusherMessage(pusherChannel, PUSHER_EVENT_KEY)
  const { appliedTeacherId, singleTeacherApplied } = useAppliedTeacher(selectedTeachers, availableTeachers, searchCount)
  const { studentFilterValue, setStudentFilterValue, studentOptions, studentsLoading } = useTeacherStudents(appliedTeacherId, STUDENTS_ENDPOINT)
  const { showSnackbar, snackbarText, displaySnackbar } = useSnackbar()
  const { momentumScoreGoal, openGoalModal, goalModal } = useMomentumScoreGoalModal(ADMIN_SKILLS_REPORT_MOMENTUM_SCORE_GOAL, displaySnackbar)

  // Group By Student requires exactly one teacher to be selected — the dataset
  // is otherwise too large to render in a single response.
  const requiresTeacherSelection = groupByValue?.value === AGGREGATION_STUDENT
  const hasSingleTeacherSelected = selectedTeachers && selectedTeachers.length === 1
  const studentViewBlocked = requiresTeacherSelection && !hasSingleTeacherSelected

  React.useEffect(() => {
    requestGet(CONCEPTS_ENDPOINT, (body) => {
      if (Array.isArray(body?.skill_categories)) {
        setAllConcepts(body.skill_categories)
        applyUrlSkillCategory(body.skill_categories)
      }
      if (Array.isArray(body?.sub_categories)) setAllSubConcepts(body.sub_categories)
    })
  }, [])

  // Fetch is driven by the parent's `searchCount` (incremented on Apply), the
  // group-by selection, and the current drill-in target. Filter dropdowns
  // changing in the sidebar should NOT trigger a fetch — they only matter once
  // the user clicks Apply.
  React.useEffect(() => {
    if (!groupByValue) return
    if (studentViewBlocked) {
      setRows([])
      setLoading(false)
      return
    }
    // Non-skill aggregations require a Skill Category. Wait for the
    // auto-select effect to populate conceptId before firing.
    if (groupByValue.value !== AGGREGATION_SKILL && !conceptId) return
    fetchReport()
  }, [searchCount, groupByValue, conceptId, subConceptId, ell, studentFilterValue.value])

  React.useEffect(() => {
    if (!pusherMessage) return
    if (filtersMatchHash(pusherMessage)) fetchReport()
  }, [pusherMessage])

  function filterPayload() {
    const isSkillAggregation = groupByValue.value === AGGREGATION_SKILL
    return {
      aggregation: groupByValue.value,
      timeframe: selectedTimeframe.value,
      school_ids: selectedSchools.map((s) => s.id),
      grades: mapItemsIfNotAll(selectedGrades, availableGrades, 'value'),
      teacher_ids: mapItemsIfNotAll(selectedTeachers, availableTeachers),
      classroom_ids: mapItemsIfNotAll(selectedClassrooms, availableClassrooms),
      concept_id: conceptId,
      sub_concept_id: isSkillAggregation ? null : subConceptId,
      ell,
      student_id: !isSkillAggregation || studentFilterValue.value === ALL_STUDENTS_OPTION.value ? null : Number(studentFilterValue.value),
    }
  }

  function fetchReport() {
    setLoading(true)
    requestPost(QUERY_ENDPOINT, filterPayload(), (body) => {
      if (body?.results) {
        setRows(body.results)
        setLoading(false)
      }
      // Otherwise the backend has enqueued a worker — Pusher will signal completion.
    })
  }

  // filterPayload's literal key order mirrors the Sidekiq worker's PayloadHasher
  // inputs: aggregation, timeframe, school_ids, then
  // AdminSkillsReport::PerformanceWorker::HASHED_FILTER_KEYS.
  function filtersMatchHash(hashMessage: string) {
    return payloadMatchesHash(Object.values(filterPayload()), hashMessage)
  }

  function handleGroupByChange(option: DropdownObjectInterface) {
    // Keep skill category across grouping changes only when the user explicitly
    // picked one. Otherwise reset to default so the new grouping starts clean —
    // the useEffect below then auto-picks the first valid option for non-skill
    // aggregations (where ALL_CATEGORIES_OPTION isn't offered).
    if (!hasExplicitlySelectedCategory) resetSkillCategory()
    setGroupByValue(option)
  }

  function handleSkillCategoryChange(option: DropdownObjectInterface) {
    const isAll = option.value === ALL_CATEGORIES_OPTION.value
    setSkillCategoryValue(option)
    setConceptId(isAll ? null : Number(option.value))
    setSubCategoryValue(ALL_SUB_CATEGORIES_OPTION)
    setSubConceptId(null)
    setHasExplicitlySelectedCategory(option.value !== ALL_CATEGORIES_OPTION.value)
  }

  // Preselect the category deep-linked via ?skill_category_id= (the Admin
  // Standards Report's skill-category chips open this report with that param).
  // Only applied when the category exists on the default gen-ed track — the
  // same constraint as picking it manually on first load. Mirrors the teacher
  // Skills Report's handling of the same param.
  function applyUrlSkillCategory(categories: SkillConcept[]) {
    const id = Number(new URLSearchParams(window.location.search).get('skill_category_id'))
    if (!Number.isInteger(id) || id <= 0) return

    const concept = categories.find((c) => Number(c.id) === id && c.gen_ed)
    if (!concept) return

    handleSkillCategoryChange({ label: concept.name, value: String(concept.id) })
  }

  function resetSkillCategory() {
    setSkillCategoryValue(ALL_CATEGORIES_OPTION)
    setConceptId(null)
    setSubCategoryValue(ALL_SUB_CATEGORIES_OPTION)
    setSubConceptId(null)
    setHasExplicitlySelectedCategory(false)
  }

  function handleSubCategoryChange(option: DropdownObjectInterface) {
    setSubCategoryValue(option)
    setSubConceptId(option.value === ALL_SUB_CATEGORIES_OPTION.value ? null : Number(option.value))
  }

  function renderSubCategoryDropdown() {
    return (
      <DropdownInput
        className="skill-sub-category-dropdown bordered small focus-on-light"
        handleChange={handleSubCategoryChange}
        isSearchable={true}
        label="Skill Sub-Category"
        options={subCategoryOptions}
        value={subCategoryValue}
      />
    )
  }

  function renderTrailingControl() {
    if (!isSkillAggregation) return renderSubCategoryDropdown()
    return (
      <StudentFilterDropdown
        disabled={isStudentFilterDisabled}
        onChange={setStudentFilterValue}
        options={studentOptions}
        value={studentFilterValue}
      />
    )
  }

  function handleEllToggle() {
    resetSkillCategory()
    setEll(!ell)
  }

  function handleViewPerformanceBreakdown() {
    setActiveView(PERFORMANCE_BREAKDOWN_VIEW)
  }

  const isSkillAggregation = groupByValue?.value === AGGREGATION_SKILL

  const displayRows = React.useMemo(() => (
    rows ? supplementMissingRows({
      rows,
      aggregation: groupByValue.value,
      conceptId,
      allConcepts,
      allSubConcepts,
      ell,
      selectedGrades,
      selectedTeachers,
    }) : rows
  ), [rows, groupByValue.value, conceptId, allConcepts, allSubConcepts, ell, selectedGrades, selectedTeachers])

  function renderBody() {
    if (studentViewBlocked) {
      return <SelectTeacherPrompt applyTeacherSelection={applyTeacherSelection} availableTeachers={availableTeachers} />
    }

    if (loading || !rows) return <Spinner />

    const sharedViewProps = {
      rows: displayRows,
      aggregation: groupByValue.value,
      goal: momentumScoreGoal,
    }

    return activeView === SMART_GROUPS_VIEW
      ? (
        <SmartGroupsView
          {...sharedViewProps}
          onViewPerformanceBreakdown={handleViewPerformanceBreakdown}
          skillScopeLabel={getUnpracticedEntityLabel(AGGREGATION_SKILL, isSkillAggregation ? conceptId : subConceptId)}
          unpracticedLabel={getUnpracticedEntityLabel(groupByValue.value, conceptId)}
        />
      )
      : (
        <PerformanceBreakdownView
          {...sharedViewProps}
          hasSelectedSkillCategory={Boolean(conceptId)}
          onSelectSkillCategory={handleSelectSkillCategoryFromRow}
          onSelectStudent={handleSelectStudentFromRow}
        />
      )
  }

  function handleSelectSkillCategoryFromRow(skillId: number | string, skillName: string) {
    handleSkillCategoryChange({ label: skillName, value: String(skillId) })
  }

  function handleSelectStudentFromRow(studentId: number | string, studentName: string) {
    handleGroupByChange(groupByDropdownOptions.find((o) => o.value === AGGREGATION_SKILL))
    setStudentFilterValue({ label: studentName, value: String(studentId) })
  }

  const skillCategoryOptions = React.useMemo(() => {
    const filtered = allConcepts.filter((c) => ell ? c.ell : c.gen_ed)
    const conceptOptions = filtered.map((c) => ({ label: c.name, value: String(c.id) }))
    return isSkillAggregation ? [ALL_CATEGORIES_OPTION, ...conceptOptions] : conceptOptions
  }, [allConcepts, ell, isSkillAggregation])

  const subCategoryOptions = React.useMemo(() => {
    if (!conceptId) return [ALL_SUB_CATEGORIES_OPTION]
    const children = allSubConcepts.filter((c) => c.parent_id === conceptId && (ell ? c.ell : c.gen_ed))
    return [ALL_SUB_CATEGORIES_OPTION, ...children.map((c) => ({ label: c.name, value: String(c.id) }))]
  }, [allSubConcepts, conceptId, ell])

  // Auto-pick the first valid category when the current selection isn't
  // available for the new grouping. Bypasses handleSkillCategoryChange so the
  // explicit-selection flag stays false — the user didn't pick this.
  React.useEffect(() => {
    if (isSkillAggregation) return
    if (!skillCategoryOptions.length) return
    const currentIsValid = skillCategoryOptions.some((o) => o.value === skillCategoryValue.value)
    if (currentIsValid) return
    const option = skillCategoryOptions[0]
    setSkillCategoryValue(option)
    setConceptId(Number(option.value))
    setSubCategoryValue(ALL_SUB_CATEGORIES_OPTION)
    setSubConceptId(null)
  }, [isSkillAggregation, skillCategoryOptions, skillCategoryValue.value])

  const isStudentFilterDisabled = !singleTeacherApplied || studentsLoading

  if (loadingFilters) return <Spinner />
  if (accessType !== FULL) return restrictedPage

  return (
    <main className="admin-skills-report">
      <div className="header">
        <h1>
          <span>Skills Report</span>
          <span className="header-breadcrumb">
            {forwardSlash}
            {VIEW_LABEL_BY_VALUE[activeView]}
            {forwardSlash}
            {`By ${groupByValue.label}`}
            {skillCategoryValue.value !== ALL_CATEGORIES_OPTION.value && (
              <React.Fragment>
                {forwardSlash}
                {skillCategoryValue.label}
              </React.Fragment>
            )}
          </span>
        </h1>
        <ReportHeaderButtons
          guideUrl="https://support.quill.org/en/articles/15926140-how-do-i-read-the-admin-skills-report-quill-premium"
          onClickChangeGoal={openGoalModal}
        />
      </div>
      <div className="view-toggle-wrapper">
        <ReportViewToggle activeView={activeView} dropdownId="admin-skills-view-toggle" onChangeView={setActiveView} />
      </div>
      <div className="dropdowns-container">
        <DropdownInput
          className="group-by-dropdown bordered small focus-on-light"
          handleChange={handleGroupByChange}
          isSearchable={true}
          label="Group by:"
          options={groupByDropdownOptions}
          value={groupByValue}
        />
        <div className="right-controls">
          <div className="ell-toggle-wrapper">
            <Checkbox
              label="Switch to ELL skills"
              onClick={handleEllToggle}
              selected={ell}
            />
          </div>
          <DropdownInput
            className="skill-category-dropdown bordered small focus-on-light"
            handleChange={handleSkillCategoryChange}
            isSearchable={true}
            label="Skill Category"
            options={skillCategoryOptions}
            value={skillCategoryValue}
          />
          {renderTrailingControl()}
        </div>
      </div>
      <div className="filter-button-container">
        <button className="interactive-wrapper focus-on-light" onClick={openMobileFilterMenu} type="button">
          <img alt={filterIcon.alt} src={filterIcon.src} />
          Filters
        </button>
      </div>
      {horizontalFilters}
      {renderBody()}
      {goalModal}
      <Snackbar text={snackbarText} visible={showSnackbar} />
    </main>
  )
}

export default AdminSkillsReportsContainer
